import { ModernAPI } from 'Modules/ModernAPI'
import debounce from 'lodash.debounce'

// Action types
export const ADMIN_ACTIONS = {
    // Admin Users
    LOAD_ADMIN_USERS_REQUEST: 'admin/loadUsersRequest',
    LOAD_ADMIN_USERS_SUCCESS: 'admin/loadUsersSuccess',
    LOAD_ADMIN_USERS_FAILURE: 'admin/loadUsersFailure',

    // Admin One User
    LOAD_ADMIN_ONE_USER_REQUEST: 'admin/loadOneUserRequest',
    LOAD_ADMIN_ONE_USER_SUCCESS: 'admin/loadOneUserSuccess',
    LOAD_ADMIN_ONE_USER_FAILURE: 'admin/loadOneUserFailure',

    // Admin Businesses
    LOAD_ADMIN_BUSINESSES_REQUEST: 'admin/loadBusinessesRequest',
    LOAD_ADMIN_BUSINESSES_SUCCESS: 'admin/loadBusinessesSuccess',
    LOAD_ADMIN_BUSINESSES_FAILURE: 'admin/loadBusinessesFailure'
}

// Helper to normalize Parse-like objects
const toJSON = (obj) => (obj && typeof obj.toJSON === 'function' ? obj.toJSON() : obj)

// Businesses
export const changePage = (page) => (dispatch) => {
    dispatch({ type: 'CHANGE_BUSINESSES_PAGE', page })
    dispatch(getAllBusinesses())
}

export const getAllBusinesses = () => (dispatch, getState) => {
    dispatch({ type: ADMIN_ACTIONS.LOAD_ADMIN_BUSINESSES_REQUEST })
    const { admin: { businesses: { perPage, page, search } } } = getState()
    return ModernAPI.getBusinessesWithProperties({ perPage, page, search })
        .then(res => {
            const total = res.total
            const businesses = (res.businesses || []).map(toJSON)
            const owners = (res.owners || []).map(toJSON)
            dispatch({ type: ADMIN_ACTIONS.LOAD_ADMIN_BUSINESSES_SUCCESS, payload: { businesses, total, owners } })
        })
        .catch(error => dispatch({ type: ADMIN_ACTIONS.LOAD_ADMIN_BUSINESSES_FAILURE, payload: { error } }))
}

export const select = (selection) => ({ type: 'SELECT_BUSINESSES', selection })

const getAllBusinessesDebounced = debounce((dispatch) => dispatch(getAllBusinesses()), 500)
export const searchBusinesses = (search) => (dispatch) => {
    dispatch({ type: 'SEARCH_BUSINESSES', search })
    if (search === '' || search.length >= 3) getAllBusinessesDebounced(dispatch)
}

export const verifyName = (objectId, verified) => (dispatch) =>
    ModernAPI.verifyBusinessName({ objectId, verified }).then(({ business }) => {
        const b = toJSON(business)
        dispatch({ type: 'UPDATE_BUSINESS_SUCCESS', business: b })
        return b
    })

export const сhangeState = (objectId, state) => (dispatch) =>
    ModernAPI.changeBusinessState({ objectId, state }).then(({ business }) => {
        const b = toJSON(business)
        dispatch({ type: 'UPDATE_BUSINESS_SUCCESS', business: b })
        return b
    })

export const deleteBusinesses = (businesses) => (dispatch) => {
    dispatch({ type: 'DELETE_BUSINESSES_REQUEST' })
    return ModernAPI.deleteBusinesses({ businesses })
        .then(() => dispatch({ type: 'DELETE_BUSINESSES_SUCCESS' }))
        .catch(error => dispatch({ type: 'DELETE_BUSINESSES_FAILURE', error }))
        .then(() => dispatch(getAllBusinesses()))
}

export const getSingleBusiness = (objectId) => () =>
    ModernAPI.adminGetBusiness({ objectId }).then(({ business, owner, templatePath }) => ({
        ...toJSON(business),
        name: (toJSON(business)?.name && toJSON(business)?.name.value) ? toJSON(business).name.value : toJSON(business).name,
        owner: toJSON(owner),
        templatePath
    }))

// Users
export const changeUsersPage = (page) => (dispatch) => {
    dispatch({ type: 'CHANGE_USERS_PAGE', page })
    dispatch(getAllUsers())
}

export const getAllUsers = () => (dispatch) => {
    dispatch({ type: ADMIN_ACTIONS.LOAD_ADMIN_USERS_REQUEST })
    const API_BASE = process.env.REACT_APP_API_URL || 'https://api.heresmyinfo.com/api'
    const token = localStorage.getItem('HMI_AUTH_TOKEN')

    const headers = {
        'Content-Type': 'application/json'
    }

    if (token) {
        headers['Authorization'] = `Bearer ${token}`
    }

    return fetch(`${API_BASE}/admin/users?t=${Date.now()}`, { headers })
        .then(res => {
            if (!res.ok) {
                console.error('Admin Users API Error:', res.status, res.statusText)
                return res.json().then(errData => {
                    throw new Error(errData?.error?.message || `HTTP ${res.status}: ${res.statusText}`)
                }).catch(() => {
                    throw new Error(`HTTP ${res.status}: ${res.statusText}`)
                })
            }
            return res.json()
        })
        .then(data => {
            console.log('Admin Users API Response:', data)
            if (!data.success) {
                throw new Error(data.error?.message || data.error || 'Failed to load users')
            }
            
            // Handle different possible response structures
            const users = data.data?.users || data.users || data.data || []
            const total = Array.isArray(users) ? users.length : 0
            console.log('Admin Users extracted:', { total, usersCount: Array.isArray(users) ? users.length : 0, firstUser: users[0] })

            if (!Array.isArray(users)) {
                console.error('Admin Users: Expected array but got:', typeof users, users)
                dispatch({ type: ADMIN_ACTIONS.LOAD_ADMIN_USERS_SUCCESS, payload: { users: [] } })
                return
            }

            // Transform users data to match table expectations
            const transformedUsers = users.map(user => ({
                ...user,
                objectId: user.id || user.objectId || user._id // Map id to objectId for table compatibility
            }))

            console.log('Admin Users transformed:', transformedUsers.length, 'users')
            dispatch({ type: ADMIN_ACTIONS.LOAD_ADMIN_USERS_SUCCESS, payload: { users: transformedUsers } })
        })
        .catch(error => {
            console.error('Admin Users fetch error:', error)
            dispatch({ type: ADMIN_ACTIONS.LOAD_ADMIN_USERS_FAILURE, payload: { error: error.message || error } })
        })
}

const getAllUsersDebounced = debounce((dispatch) => dispatch(getAllUsers()), 500)
export const selectUsers = (selection) => ({ type: 'SELECT_USERS', selection })

export const searchUsers = (search) => (dispatch) => {
    dispatch({ type: 'SEARCH_USERS', search })
    // Reload users when search changes
    if (search === '' || search.length >= 3) {
        getAllUsersDebounced(dispatch)
    }
}

export const deleteUsers = (users) => (dispatch) => {
    dispatch({ type: 'DELETE_USERS_REQUEST' })
    return ModernAPI.deleteUsers({ users })
        .then(() => {
            dispatch({ type: 'DELETE_USERS_SUCCESS' })
            // Reload users list after successful deletion
            dispatch(getAllUsers())
        })
        .catch(error => {
            console.error('Error deleting users:', error)
            dispatch({ type: 'DELETE_USERS_FAILURE', error: error.message || error })
        })
}

export const getAdminSingleUser = (userId) => (dispatch) => {
    dispatch({ type: 'GET_USER_REQUEST' })
    let user
    return ModernAPI.getUserWithProperties(userId)
        .then(res => { user = toJSON(res.user || res); return ModernAPI.getConnectionsForUsers({ users: [userId], properties: ['fn'] }) })
        .then(res => {
            const rawConnections = res.connections || res.data?.connections || []
            const rawProperties = res.properties || res.data?.properties || []
            const connections = rawConnections.map(toJSON)
            const names = rawProperties.map(toJSON)
            dispatch({ type: 'GET_USER_SUCCESS', user, connections, names })
        })
        .catch(error => dispatch({ type: 'GET_USER_FAILURE', error }))
}

export const deleteConnections = (userId, connections) => (dispatch) => {
    dispatch({ type: 'DELETE_CONNECTIONS_REQUEST' })
    return ModernAPI.deleteConnections({ connections })
        .then(() => dispatch({ type: 'DELETE_CONNECTIONS_SUCCESS' }))
        .catch(error => dispatch({ type: 'DELETE_CONNECTIONS_FAILURE', error }))
        .then(() => dispatch(getAdminSingleUser(userId)))
}

export const toggleUser = (userId, field, value) => (dispatch) => {
    dispatch({ type: 'TOGGLE_USER_REQUEST' })
    return ModernAPI.toggleUserField({ userId, field, value })
        .then(() => dispatch({ type: 'TOGGLE_USER_SUCCESS' }))
        .catch(error => dispatch({ type: 'TOGGLE_USER_FAILURE', error }))
        .then(() => dispatch(getAllUsers()))
}

// QR Codes
export const loadQRCodes = () => async (dispatch) => {
    try {
        const data = await ModernAPI.getAllQRCodesWithoutUser()
        return data
    } catch (error) {
        console.error('loadQRCodes error:', error)
        throw error
    }
}

export const generateQRCodes = (amount, label) => () => {
    return ModernAPI.generateQRCodes({ amount, label })
        .then(data => (data && data.list ? data.list : []).map(c => (c && typeof c.toJSON === 'function' ? c.toJSON() : c)))
}

// Initial state
export const initialState = {
    users: {
        list: [],
        loading: false,
        error: null,
        deleteError: null,
        total: 0,
        page: 0,
        perPage: 10,
        search: '',
        selection: []
    },
    user: {
        data: null,
        loading: false,
        error: null
    },
    businesses: {
        list: [],
        loading: false,
        error: null
    }
}

// Modern reducer using switch statement with better organization
export const adminReducer = (state = initialState, action) => {
    console.log('Admin reducer received action:', action.type, action)
    switch (action.type) {
        // Single User & Connections Management
        case 'GET_USER_REQUEST':
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: true,
                    error: null,
                    deleteConnectionsError: null
                }
            }

        case 'GET_USER_SUCCESS': {
            try {
                const names = Array.isArray(action.names) ? action.names : []
                const namesMap = new Map(names.map(u => {
                    if (u && u.objectId && u.profile && u.profile.properties) {
                        const nameProperty = u.profile.properties
                            .filter(property => property && (property.name === 'givenName' || property.name === 'familyName'))
                            .map(property => property.value)
                            .join(' ')
                        return [u.objectId, nameProperty]
                    }
                    return [u && u.objectId, 'Unknown']
                }))

                const connections = Array.isArray(action.connections) ? action.connections : []
                const connectionsByDirection = connections.reduce((res, connection) => {
                    if (connection && connection.fromPerson && connection.toPerson) {
                        if (connection.fromPerson.objectId === action.user.objectId) {
                            if (namesMap.has(connection.toPerson.objectId)) {
                                connection.fullName = namesMap.get(connection.toPerson.objectId)
                            }
                            res.outgoing.push(connection)
                        } else {
                            if (namesMap.has(connection.fromPerson.objectId)) {
                                connection.fullName = namesMap.get(connection.fromPerson.objectId)
                            }
                            res.incoming.push(connection)
                        }
                    }
                    return res
                }, { outgoing: [], incoming: [] })

                return {
                    ...state,
                    user: {
                        ...state.user,
                        loading: false,
                        error: null,
                        user: {
                            ...action.user,
                            connections: connectionsByDirection
                        }
                    }
                }
            } catch (error) {
                console.error('Error in GET_USER_SUCCESS reducer:', error)
                return {
                    ...state,
                    user: {
                        ...state.user,
                        loading: false,
                        error: error.message,
                        user: {
                            ...action.user,
                            connections: { outgoing: [], incoming: [] }
                        }
                    }
                }
            }
        }

        case 'GET_USER_FAILURE':
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: false,
                    error: action.error
                }
            }

        case 'DELETE_CONNECTIONS_REQUEST':
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: true,
                    deleteConnectionsError: null
                }
            }

        case 'DELETE_CONNECTIONS_SUCCESS':
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: false,
                    deleteConnectionsError: null
                }
            }

        case 'DELETE_CONNECTIONS_FAILURE':
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: false,
                    deleteConnectionsError: action.error || { message: 'Failed to delete connections' }
                }
            }
        // Admin Users
        case ADMIN_ACTIONS.LOAD_ADMIN_USERS_REQUEST:
            return {
                ...state,
                users: {
                    ...state.users,
                    loading: true,
                    error: null
                }
            }

        case ADMIN_ACTIONS.LOAD_ADMIN_USERS_SUCCESS:
            console.log('Admin reducer received LOAD_ADMIN_USERS_SUCCESS:', action.payload)
            const users = action.payload.users || []
            return {
                ...state,
                users: {
                    ...state.users,
                    loading: false,
                    list: users,
                    total: users.length,
                    page: state.users.page || 0,
                    perPage: state.users.perPage || 10,
                    error: null
                }
            }

        case ADMIN_ACTIONS.LOAD_ADMIN_USERS_FAILURE:
            return {
                ...state,
                users: {
                    ...state.users,
                    loading: false,
                    error: action.payload.error
                }
            }

        case 'CHANGE_USERS_PAGE':
            return {
                ...state,
                users: {
                    ...state.users,
                    page: action.page || 0
                }
            }

        case 'SEARCH_USERS':
            return {
                ...state,
                users: {
                    ...state.users,
                    search: action.search || ''
                }
            }

        case 'SELECT_USERS':
            return {
                ...state,
                users: {
                    ...state.users,
                    selection: action.selection || []
                }
            }

        case 'DELETE_USERS_REQUEST':
            return {
                ...state,
                users: {
                    ...state.users,
                    loading: true,
                    deleteError: null
                }
            }

        case 'DELETE_USERS_SUCCESS':
            return {
                ...state,
                users: {
                    ...state.users,
                    loading: false,
                    deleteError: null,
                    selection: [] // Clear selection after successful delete
                }
            }

        case 'DELETE_USERS_FAILURE':
            return {
                ...state,
                users: {
                    ...state.users,
                    loading: false,
                    deleteError: action.error || action.payload?.error || { message: 'Failed to delete users' }
                }
            }

        // Admin One User
        case ADMIN_ACTIONS.LOAD_ADMIN_ONE_USER_REQUEST:
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: true,
                    error: null
                }
            }

        case ADMIN_ACTIONS.LOAD_ADMIN_ONE_USER_SUCCESS:
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: false,
                    data: action.payload.user
                }
            }

        case ADMIN_ACTIONS.LOAD_ADMIN_ONE_USER_FAILURE:
            return {
                ...state,
                user: {
                    ...state.user,
                    loading: false,
                    error: action.payload.error
                }
            }

        // Admin Businesses
        case ADMIN_ACTIONS.LOAD_ADMIN_BUSINESSES_REQUEST:
            return {
                ...state,
                businesses: {
                    ...state.businesses,
                    loading: true,
                    error: null
                }
            }

        case ADMIN_ACTIONS.LOAD_ADMIN_BUSINESSES_SUCCESS:
            return {
                ...state,
                businesses: {
                    ...state.businesses,
                    loading: false,
                    rows: action.payload.businesses,
                    list: action.payload.businesses,
                    total: action.payload.total,
                    owners: action.payload.owners
                }
            }

        case ADMIN_ACTIONS.LOAD_ADMIN_BUSINESSES_FAILURE:
            return {
                ...state,
                businesses: {
                    ...state.businesses,
                    loading: false,
                    error: action.payload.error
                }
            }

        default:
            return state
    }
}

export default adminReducer

